import './Glossary.scss'
import { useEffect, useState } from 'react'
import { useDotnetClient } from '@/contexts/DotnetClientContext'
import { Loading } from '@/ui'
import { SupportPanelState } from '@/layouts/UnityLayout'
import { MessagesGlossaryGlossary } from '@/dotnet-client'
import { useGetSearchParam } from '@/hooks'
import { SearchBox } from '@/ux/SearchBox'

type GlossaryItemProps = {
  item: MessagesGlossaryGlossary
  onClick: () => void
  selected: boolean
}

const GlossaryItem = (props: GlossaryItemProps) => {
  const classes = ['glossary-item', props.selected && 'selected'].filter(Boolean).join(' ')

  return (
    <div className={classes} onClick={props.onClick}>
      <div className="glossary-title">{props.item.term}</div>
      <div className="glossary-description">{props.item.short ?? props.item.long}</div>
    </div>
  )
}

export const Glossary = ({ supportPanelView, setSupportPanelView }: SupportPanelState) => {
  const appId = useGetSearchParam<number>('appId')
  const [glossary, setGlossary] = useState<MessagesGlossaryGlossary[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [selected, setSelected] = useState<number>(0)
  const [searchString, setSearchString] = useState('')
  const { dmpClient } = useDotnetClient()

  useEffect(() => {
    if (!appId) return
    if (!dmpClient) return

    setLoading(true)
    dmpClient.glossaryApi
      .getGlossaryApiGetGlossary(appId)
      .then((_glossary) => {
        setGlossary(_glossary)
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false))
  }, [appId, dmpClient])

  const filterOnSearchTerm = (term: MessagesGlossaryGlossary) =>
    !searchString || searchString === '' || JSON.stringify(term).toLowerCase().includes(searchString.toLowerCase())

  const numberOfFiles = () => {
    if (!glossary) return ''
    const total = glossary.filter(filterOnSearchTerm).length ?? 0
    const string = total === 1 ? 'term' : 'terms'
    return `${total} ${string}`
  }

  const onSelect = (index: number) => {
    setSupportPanelView({ key: 'glossary', size: 'expanded' })
    setSelected(index)
  }

  const classes = ['ux-glossary-layout', supportPanelView?.size].filter(Boolean).join(' ')

  return (
    <div className={classes}>
      <div className="glossary">
        <SearchBox searchString={searchString} setSearchString={setSearchString} />
        <div className="file-count" key="file_count">
          {numberOfFiles()}
        </div>
        {loading && <Loading />}
        {!loading && glossary.filter(filterOnSearchTerm).length === 0 && <Loading message="No glossary items" />}
        {!loading &&
          glossary.filter(filterOnSearchTerm).length > 0 &&
          glossary
            .filter(filterOnSearchTerm)
            .map((term, index) => (
              <GlossaryItem key={term.id} item={term} onClick={() => onSelect(index)} selected={index === selected} />
            ))}
      </div>
      {supportPanelView?.size === 'expanded' && !loading && glossary.length > 0 && (
        <div className="glossary-long">
          <div className="glossary-title">{glossary[selected].term}</div>
          <div className="glossary-long-description">{glossary[selected].long ?? glossary[selected].short}</div>
        </div>
      )}
    </div>
  )
}
